import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    CircleDot,
    ChevronRight,
    Heart,
    Home,
    Sparkles,
    ArrowLeft
} from 'lucide-react';
import { PrimaryButton, SecondaryButton, TertiaryButton } from '../components/brand/BrandButton';
import { BrandCard } from '../components/brand/BrandCard';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { IconContainer } from '../components/brand/BrandIcon';
import { GentleOptions } from '../components/brand/GentleOptions';
import { ModuleHeader, SafetyFooter } from '../components/brand/QuickExit';
import { PersistentBottomBar, BottomBarSpacer } from '../components/brand/PersistentBottomBar';

const WhichCycleAreWeIn = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState('intro');
    const [answers, setAnswers] = useState({
        pursueWithdraw: null,
        blameBlame: null,
        withdrawWithdraw: null
    });

    const answerOptions = [
        { id: 'yes', label: 'Yes' },
        { id: 'sometimes', label: 'Sometimes' },
        { id: 'no', label: 'No' }
    ];

    const handleAnswer = (question, answer) => {
        setAnswers(prev => ({ ...prev, [question]: answer }));
        
        // Determine next step based on answer
        if (question === 'pursueWithdraw') {
            if (answer === 'yes' || answer === 'sometimes') {
                setStep('pursue-withdraw-insight');
            } else {
                setStep('blame-blame-q');
            }
        } else if (question === 'blameBlame') {
            if (answer === 'yes' || answer === 'sometimes') {
                setStep('blame-blame-insight');
            } else {
                setStep('withdraw-withdraw-q');
            }
        } else if (question === 'withdrawWithdraw') {
            if (answer === 'yes' || answer === 'sometimes') {
                setStep('withdraw-withdraw-insight');
            } else {
                setStep('underlying');
            }
        }
    };

    const continueFromInsight = (currentInsight) => {
        if (currentInsight === 'pursue-withdraw-insight') {
            setStep('blame-blame-q');
        } else if (currentInsight === 'blame-blame-insight') {
            setStep('withdraw-withdraw-q');
        } else if (currentInsight === 'withdraw-withdraw-insight') {
            setStep('underlying');
        }
    };

    const handleBackNavigation = () => {
        const backMap = {
            'intro': () => navigate('/relationship-hub'),
            'pursue-withdraw-q': () => setStep('intro'),
            'pursue-withdraw-insight': () => setStep('pursue-withdraw-q'),
            'blame-blame-q': () => setStep('pursue-withdraw-q'),
            'blame-blame-insight': () => setStep('blame-blame-q'),
            'withdraw-withdraw-q': () => setStep('blame-blame-q'),
            'withdraw-withdraw-insight': () => setStep('withdraw-withdraw-q'),
            'underlying': () => setStep('withdraw-withdraw-q'),
            'whathelps': () => setStep('underlying'),
            'reframe': () => setStep('whathelps')
        };
        const action = backMap[step];
        if (action) action();
    };

    return (
        <div className="min-h-screen bg-transparent">
            {/* Orenda Module Header */}
            <ModuleHeader
                title="Which Cycle Are We In?"
                onBack={handleBackNavigation}
                backDestination="/relationship-hub"
                exitDestination="/home?tab=relationships"
                showBack={step !== 'reframe'}
            />

            <main className="max-w-xl mx-auto px-6 py-8">
                {/* INTRO */}
                {step === 'intro' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-10">
                            <IconContainer variant="lavender" size="xl" useGradient={true} className="mx-auto mb-5">
                                <CircleDot className="w-8 h-8" />
                            </IconContainer>
                            <BodyText className="text-[var(--orenda-text-secondary)] max-w-md mx-auto leading-relaxed mb-3">
                                Sometimes relationships have patterns that repeat — moments where you both feel stuck or misunderstood.
                            </BodyText>
                            <SmallText className="text-[var(--orenda-text-muted)] max-w-sm mx-auto leading-relaxed">
                                This is a gentle exploration. There are no right or wrong answers, and nothing here is a diagnosis.
                            </SmallText>
                        </div>
                        
                        <div className="mt-8 text-center">
                            <SmallText className="text-[var(--orenda-text-muted)] italic">
                                Take your time with this.
                            </SmallText>
                        </div>
                        
                        <BottomBarSpacer height="default" />
                    </div>
                )}
                
                {/* Persistent Bottom Bar for Intro */}
                {step === 'intro' && (
                    <PersistentBottomBar>
                        <PrimaryButton
                            onClick={() => setStep('pursue-withdraw-q')}
                            className="w-full"
                            size="action"
                            data-testid="start-btn"
                        >
                            Begin exploring
                            <ChevronRight className="w-4 h-4 ml-1.5" />
                        </PrimaryButton>
                    </PersistentBottomBar>
                )}

                {/* STEP 1: PURSUE-WITHDRAW QUESTION */}
                {step === 'pursue-withdraw-q' && (
                    <div className="animate-fade-in">
                        {/* Progress indicator */}
                        <div className="flex justify-center gap-2 mb-8">
                            <div className="w-2 h-2 rounded-full bg-[#7D8E95]" />
                            <div className="w-2 h-2 rounded-full bg-[var(--orenda-border)]" />
                            <div className="w-2 h-2 rounded-full bg-[var(--orenda-border)]" />
                        </div>
                        
                        <div className="glass-card rounded-2xl p-6 mb-8">
                            <BodyText className="text-[var(--orenda-text-primary)] text-lg text-center leading-relaxed">
                                When things feel tense, does one of you tend to reach out while the other needs more space?
                            </BodyText>
                        </div>

                        <GentleOptions
                            options={answerOptions}
                            selectedId={answers.pursueWithdraw}
                            onSelect={(id) => handleAnswer('pursueWithdraw', id)}
                            showUnsure={true}
                            unsureLabel="I'm not sure"
                        />

                        <div className="mt-6 text-center">
                            <SmallText className="text-[var(--orenda-text-muted)] italic">
                                There's no wrong answer here.
                            </SmallText>
                        </div>

                        <SafetyFooter
                            destination="/relationship-hub"
                            exitLabel="Come back later"
                            reassurance="This is just for reflection."
                        />
                    </div>
                )}

                {/* STEP 1: PURSUE-WITHDRAW INSIGHT */}
                {step === 'pursue-withdraw-insight' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-6">
                            <IconContainer variant="lavender" size="lg" useGradient={true} className="mx-auto mb-4">
                                <Sparkles className="w-5 h-5" />
                            </IconContainer>
                        </div>

                        <div className="glass-card-lavender rounded-2xl p-6 mb-6">
                            <SmallText className="text-[var(--orenda-text-muted)] text-center block mb-3">
                                This is common.
                            </SmallText>
                            <BodyText className="text-[var(--orenda-text-primary)] text-center leading-relaxed">
                                When one person reaches and the other steps back, both usually feel alone in different ways — one longing for closeness, the other feeling overwhelmed or unsure what to do.
                            </BodyText>
                        </div>
                        
                        <div className="text-center mb-6">
                            <SmallText className="text-[var(--orenda-text-muted)] italic">
                                Neither response is wrong. It's just the dance you've learned.
                            </SmallText>
                        </div>

                        <BottomBarSpacer height="default" />
                    </div>
                )}
                
                {/* Persistent Bottom Bar for Pursue-Withdraw Insight */}
                {step === 'pursue-withdraw-insight' && (
                    <PersistentBottomBar>
                        <PrimaryButton
                            onClick={() => continueFromInsight('pursue-withdraw-insight')}
                            className="w-full"
                            size="action"
                        >
                            Continue
                            <ChevronRight className="w-4 h-4 ml-1.5" />
                        </PrimaryButton>
                    </PersistentBottomBar>
                )}

                {/* STEP 2: BLAME-BLAME QUESTION */}
                {step === 'blame-blame-q' && (
                    <div className="animate-fade-in">
                        {/* Progress indicator */}
                        <div className="flex justify-center gap-2 mb-8">
                            <div className="w-2 h-2 rounded-full bg-[#7D8E95]" />
                            <div className="w-2 h-2 rounded-full bg-[#7D8E95]" />
                            <div className="w-2 h-2 rounded-full bg-[var(--orenda-border)]" />
                        </div>
                        
                        <div className="glass-card rounded-2xl p-6 mb-8">
                            <BodyText className="text-[var(--orenda-text-primary)] text-lg text-center leading-relaxed">
                                Do conversations sometimes turn into both of you defending yourselves or trying to be understood?
                            </BodyText>
                        </div>

                        <GentleOptions
                            options={answerOptions}
                            selectedId={answers.blameBlame}
                            onSelect={(id) => handleAnswer('blameBlame', id)}
                            showUnsure={true}
                            unsureLabel="I'm not sure"
                        />

                        <div className="mt-6 text-center">
                            <SmallText className="text-[var(--orenda-text-muted)] italic">
                                Just notice what feels true.
                            </SmallText>
                        </div>

                        <SafetyFooter
                            destination="/relationship-hub"
                            exitLabel="Come back later"
                            reassurance="This is just for reflection."
                        />
                    </div>
                )}

                {/* STEP 2: BLAME-BLAME INSIGHT */}
                {step === 'blame-blame-insight' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-6">
                            <IconContainer variant="rose" size="lg" useGradient={true} className="mx-auto mb-4">
                                <Sparkles className="w-5 h-5" />
                            </IconContainer>
                        </div>

                        <div className="glass-card-rose rounded-2xl p-6 mb-6">
                            <SmallText className="text-[var(--orenda-text-muted)] text-center block mb-3">
                                This makes sense.
                            </SmallText>
                            <BodyText className="text-[var(--orenda-text-primary)] text-center leading-relaxed">
                                When both people are defending, it's usually because both feel hurt or misunderstood underneath. The loudness is often a way of trying to be heard.
                            </BodyText>
                        </div>
                        
                        <div className="text-center mb-6">
                            <SmallText className="text-[var(--orenda-text-muted)] italic">
                                It doesn't mean anyone is wrong. It means you're both hurting.
                            </SmallText>
                        </div>

                        <BottomBarSpacer height="default" />
                    </div>
                )}
                
                {/* Persistent Bottom Bar for Blame-Blame Insight */}
                {step === 'blame-blame-insight' && (
                    <PersistentBottomBar>
                        <PrimaryButton
                            onClick={() => continueFromInsight('blame-blame-insight')}
                            className="w-full"
                            size="action"
                        >
                            Continue
                            <ChevronRight className="w-4 h-4 ml-1.5" />
                        </PrimaryButton>
                    </PersistentBottomBar>
                )}

                {/* STEP 3: WITHDRAW-WITHDRAW QUESTION */}
                {step === 'withdraw-withdraw-q' && (
                    <div className="animate-fade-in">
                        {/* Progress indicator */}
                        <div className="flex justify-center gap-2 mb-8">
                            <div className="w-2 h-2 rounded-full bg-[#7D8E95]" />
                            <div className="w-2 h-2 rounded-full bg-[#7D8E95]" />
                            <div className="w-2 h-2 rounded-full bg-[#7D8E95]" />
                        </div>
                        
                        <div className="glass-card rounded-2xl p-6 mb-8">
                            <BodyText className="text-[var(--orenda-text-primary)] text-lg text-center leading-relaxed">
                                Do you both tend to go quiet or step back when things feel hard, leaving things unspoken?
                            </BodyText>
                        </div>

                        <GentleOptions
                            options={answerOptions}
                            selectedId={answers.withdrawWithdraw}
                            onSelect={(id) => handleAnswer('withdrawWithdraw', id)}
                            showUnsure={true}
                            unsureLabel="I'm not sure"
                        />

                        <div className="mt-6 text-center">
                            <SmallText className="text-[var(--orenda-text-muted)] italic">
                                Almost done.
                            </SmallText>
                        </div>

                        <SafetyFooter
                            destination="/relationship-hub"
                            exitLabel="Come back later"
                            reassurance="This is just for reflection."
                        />
                    </div>
                )}

                {/* STEP 3: WITHDRAW-WITHDRAW INSIGHT */}
                {step === 'withdraw-withdraw-insight' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-6">
                            <IconContainer variant="blue" size="lg" useGradient={true} className="mx-auto mb-4">
                                <Sparkles className="w-5 h-5" />
                            </IconContainer>
                        </div>

                        <div className="glass-card-sage rounded-2xl p-6 mb-6">
                            <SmallText className="text-[var(--orenda-text-muted)] text-center block mb-3">
                                This is tender.
                            </SmallText>
                            <BodyText className="text-[var(--orenda-text-primary)] text-center leading-relaxed">
                                When both people go quiet, it's often because neither knows how to bridge the gap without making things worse. The silence can feel heavy, but it usually comes from care, not indifference.
                            </BodyText>
                        </div>
                        
                        <div className="text-center mb-6">
                            <SmallText className="text-[var(--orenda-text-muted)] italic">
                                Wanting peace isn't the same as giving up.
                            </SmallText>
                        </div>

                        <BottomBarSpacer height="default" />
                    </div>
                )}
                
                {/* Persistent Bottom Bar for Withdraw-Withdraw Insight */}
                {step === 'withdraw-withdraw-insight' && (
                    <PersistentBottomBar>
                        <PrimaryButton
                            onClick={() => continueFromInsight('withdraw-withdraw-insight')}
                            className="w-full"
                            size="action"
                        >
                            Continue
                            <ChevronRight className="w-4 h-4 ml-1.5" />
                        </PrimaryButton>
                    </PersistentBottomBar>
                )}

                {/* STEP 4: UNDERLYING EMOTIONS */}
                {step === 'underlying' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-6">
                            <IconContainer variant="rose" size="xl" useGradient={true} className="mx-auto mb-4">
                                <Heart className="w-6 h-6" />
                            </IconContainer>
                        </div>

                        <div className="glass-card-rose rounded-2xl p-6 mb-6">
                            <BodyText className="text-[var(--orenda-text-primary)] text-center leading-relaxed text-lg mb-4">
                                Whatever pattern feels familiar, there are usually softer feelings underneath — things like fear, loneliness, shame, or longing.
                            </BodyText>
                            <div className="pt-4 border-t border-white/30">
                                <SmallText className="text-[var(--orenda-text-secondary)] text-center block font-medium">
                                    The pattern is just what happens on the surface.
                                </SmallText>
                            </div>
                        </div>
                        
                        <div className="text-center mb-6">
                            <SmallText className="text-[var(--orenda-text-muted)] italic">
                                The cycle is the problem — not either of you.
                            </SmallText>
                        </div>

                        <BottomBarSpacer height="default" />
                    </div>
                )}
                
                {/* Persistent Bottom Bar for Underlying */}
                {step === 'underlying' && (
                    <PersistentBottomBar>
                        <PrimaryButton
                            onClick={() => setStep('whathelps')}
                            className="w-full"
                            size="action"
                        >
                            Continue
                            <ChevronRight className="w-4 h-4 ml-1.5" />
                        </PrimaryButton>
                    </PersistentBottomBar>
                )}

                {/* STEP 5: WHAT HELPS */}
                {step === 'whathelps' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-8">
                            <Heading as="h2" size="text-xl" className="mb-2">
                                Some things that can help
                            </Heading>
                            <SmallText className="text-[var(--orenda-text-muted)]">
                                These are invitations, not instructions.
                            </SmallText>
                        </div>

                        <div className="space-y-4 mb-8">
                            <div className="glass-card-sage rounded-2xl p-5">
                                <div className="flex items-start gap-3">
                                    <div className="w-2 h-2 rounded-full bg-[var(--orenda-soft-teal)] mt-2 flex-shrink-0" />
                                    <BodyText className="text-[var(--orenda-text-primary)] leading-relaxed">
                                        Notice when the pattern is happening — even naming it can help slow things down.
                                    </BodyText>
                                </div>
                            </div>
                            
                            <div className="glass-card-rose rounded-2xl p-5">
                                <div className="flex items-start gap-3">
                                    <div className="w-2 h-2 rounded-full bg-[var(--orenda-blush-rose)] mt-2 flex-shrink-0" />
                                    <BodyText className="text-[var(--orenda-text-primary)] leading-relaxed">
                                        Try sharing the softer feeling underneath, when you're ready — "I feel scared" instead of "You never listen."
                                    </BodyText>
                                </div>
                            </div>
                            
                            <div className="glass-card rounded-2xl p-5">
                                <div className="flex items-start gap-3">
                                    <div className="w-2 h-2 rounded-full bg-[#7D8E95] mt-2 flex-shrink-0" />
                                    <BodyText className="text-[var(--orenda-text-primary)] leading-relaxed">
                                        Pause when things escalate. Let both nervous systems settle before continuing.
                                    </BodyText>
                                </div>
                            </div>
                        </div>

                        <BottomBarSpacer height="default" />
                    </div>
                )}
                
                {/* Persistent Bottom Bar for What Helps */}
                {step === 'whathelps' && (
                    <PersistentBottomBar>
                        <PrimaryButton
                            onClick={() => setStep('reframe')}
                            className="w-full"
                            size="action"
                        >
                            Continue
                            <ChevronRight className="w-4 h-4 ml-1.5" />
                        </PrimaryButton>
                    </PersistentBottomBar>
                )}

                {/* STEP 6: HOPEFUL REFRAME */}
                {step === 'reframe' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-6">
                            <IconContainer variant="sage" size="xl" useGradient={true} className="mx-auto mb-4">
                                <Heart className="w-6 h-6" />
                            </IconContainer>
                        </div>

                        <div className="glass-card-sage rounded-2xl p-6 mb-6">
                            <BodyText className="text-[var(--orenda-text-primary)] text-center leading-relaxed text-lg">
                                Recognising the pattern is the first step. It doesn't fix everything, but it can help you both feel less alone in it.
                            </BodyText>
                        </div>
                        
                        <div className="text-center mb-8">
                            <SmallText className="text-[var(--orenda-text-muted)] italic">
                                You're not broken. You're just human, trying to find each other.
                            </SmallText>
                        </div>

                        <BottomBarSpacer height="extended" />
                    </div>
                )}
                
                {/* Persistent Bottom Bar for Reframe */}
                {step === 'reframe' && (
                    <PersistentBottomBar>
                        <PrimaryButton
                            onClick={() => navigate('/regulate/relationship-cycles')}
                            className="w-full"
                            size="action"
                        >
                            Learn more about cycles
                            <ChevronRight className="w-4 h-4 ml-1.5" />
                        </PrimaryButton>
                        <SecondaryButton
                            onClick={() => navigate('/relationship-hub')}
                            className="w-full"
                            size="action"
                            data-testid="return-to-hub-btn"
                        >
                            <Home className="w-4 h-4 mr-1.5" />
                            Return to Hub
                        </SecondaryButton>
                    </PersistentBottomBar>
                )}

            </main>
        </div>
    );
};

export default WhichCycleAreWeIn;
